import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, money } from "@/lib/api";

const BLANK = { code: "", name: "", price: 0, max_staff: 10, description: "", features: [], highlighted: false, active: true, sort: 10 };

const PlanForm = ({ plan, onSaved }) => {
  const [f, setF] = useState({ ...plan, featuresText: (plan.features || []).join("\n") });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { code: f.code, name: f.name, price: Number(f.price), max_staff: Number(f.max_staff), description: f.description,
      features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean), highlighted: f.highlighted, active: f.active, sort: Number(f.sort) };
    try {
      if (plan.id) await api.put(`/super/plans/${plan.id}`, body);
      else await api.post("/super/plans", body);
      toast.success("Plan saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Code</Label><Input required disabled={!!plan.id} value={f.code} onChange={set("code")} placeholder="e.g. pro" data-testid="plan-code-input" /></div>
        <div className="space-y-1.5"><Label>Name</Label><Input required value={f.name} onChange={set("name")} data-testid="plan-name-input" /></div>
        <div className="space-y-1.5"><Label>Price £/month</Label><Input type="number" step="0.01" min="0" value={f.price} onChange={set("price")} data-testid="plan-price-input" /></div>
        <div className="space-y-1.5"><Label>Max staff (0 = unlimited)</Label><Input type="number" min="0" value={f.max_staff} onChange={set("max_staff")} data-testid="plan-max-staff-input" /></div>
      </div>
      <div className="space-y-1.5"><Label>Description</Label><Input value={f.description} onChange={set("description")} data-testid="plan-description-input" /></div>
      <div className="space-y-1.5"><Label>Features (one per line)</Label><Textarea rows={4} value={f.featuresText} onChange={set("featuresText")} data-testid="plan-features-input" /></div>
      <div className="flex gap-6 text-sm">
        <label className="flex items-center gap-2"><Switch checked={f.highlighted} onCheckedChange={(v) => setF({ ...f, highlighted: v })} data-testid="plan-highlight-switch" />Highlight</label>
        <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="plan-active-switch" />Visible on site</label>
      </div>
      <Button type="submit" className="w-full rounded-full" data-testid="plan-save-btn">Save plan</Button>
    </form>
  );
};

export default function SuperPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/super/plans").then((r) => setPlans(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader title="Plans" subtitle="Prices in GBP. Paid plans sync to Stripe automatically.">
        <Button className="rounded-full" onClick={() => setEditing(BLANK)} data-testid="new-plan-btn"><Plus className="mr-1 h-4 w-4" />New plan</Button>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`plan-card-${p.code}`}>
            <div className="flex items-center justify-between">
              <p className="font-display text-xl font-bold">{p.name}</p>
              <button onClick={() => setEditing(p)} className="text-slate-400 hover:text-indigo-600" data-testid={`edit-plan-btn-${p.code}`}><Pencil className="h-4 w-4" /></button>
            </div>
            <p className="mt-2 font-display text-3xl font-extrabold">{money(p.price)}<span className="text-sm font-medium text-slate-400">/mo</span></p>
            <p className="mt-1 text-sm text-slate-500">{p.max_staff ? `Up to ${p.max_staff} staff` : "Unlimited staff"}</p>
            <div className="mt-3 flex gap-2">
              {!p.active && <Badge variant="secondary">Hidden</Badge>}
              {p.highlighted && <Badge className="bg-indigo-600">Highlighted</Badge>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
          {editing && <PlanForm plan={editing} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
